import { SprintProductEntity } from '@data';
import ExpandableContent from '@app/components/shared/expandableContent';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useDisputedBy, useRemarkAnsweredBy } from './ApprovalSummaryDialog';
import { ReportNoteBox } from './ReportNoteBox';

/**
 * The remark a dispute was made of, standing beside the summary it is about, so what has to change is
 * next to the thing to change rather than behind a button.
 *
 * Dashed rather than solid, in the warning colour: every other box on this card is a settled part of the
 * report, and this one is the opposite — it is here until the remark has been dealt with and the report
 * goes back for approval.
 *
 * Answering it does not remove it. It goes plain instead, because it has stopped being work and started
 * being the question our answer was to — which is what anybody reads if the client comes back disagreeing.
 */
export const DisputeRemarkNote = ({
  sprintProduct,
  settled = false,
}: {
  sprintProduct: SprintProductEntity;
  settled?: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const disputedBy = useDisputedBy(sprintProduct);
  const answeredBy = useRemarkAnsweredBy(sprintProduct);

  const resolution = sprintProduct.disputeResolution;

  return (
    <ReportNoteBox title={t('disputeRemarkLabel')} meta={disputedBy} tone={settled ? 'plain' : 'warning'}>
      <ExpandableContent content={sprintProduct.comment} />
      {/* Otherwise a plain box beside the summary reads as a remark still waiting for an answer. */}
      {settled && (
        <div className="mt-3 border-t border-border pt-2">
          {/* Which of the two answers it got, before who gave it. The client is the one reader who cannot
              work this out from anywhere else on the page: from their side a dismissal and a changed
              report both end with the report back in front of them asking to be approved. */}
          {resolution && <p className="text-xs font-medium text-foreground">{t(`disputeAnswer.${resolution}`)}</p>}
          {sprintProduct.disputeResolutionNote && (
            <p className="mt-1 whitespace-pre-line text-xs text-foreground">{sprintProduct.disputeResolutionNote}</p>
          )}
          {answeredBy && (
            <p className="mt-1 text-xs text-muted-foreground">{t('remarkAnsweredBy', { by: answeredBy })}</p>
          )}
        </div>
      )}
    </ReportNoteBox>
  );
};
